import { Box, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import type { WidaStudent } from '../Upload/widaHistory';
import { BelowExitPill, Pill, PlanCard, SectionLabel, TrendPill } from './components';
import { WidaTrajectoryChart } from '../ProgramEvaluation/components/charts/WidaTrajectoryChart';
import { PER_DOMAIN_CHART_FONT_SIZE } from '../ProgramEvaluation/components/PerDomainCard';
import type { PeTrajectoryPoint } from '../ProgramEvaluation/types';
import { COLORS, WIDA_ONLY_PILL } from './tokens';
import { currentDescriptor } from './planContent';
import {
  flashlightBenchmark,
  getFlashlightData,
  isFlashlightDomain,
  type FlashlightData,
  type WindowScores,
} from './flashlight';
import {
  DOMAIN_LABELS,
  belowExit,
  currentLevel,
  detectResetYear,
  domainSeries,
  domainTrend,
  priorLevel,
  type DomainKey,
  type PlanStudent,
} from './derive';
import { useReclassCriteria } from './reclassCriteriaContext';

function trendPill(student: WidaStudent, domain: DomainKey) {
  const t = domainTrend(student, domain);
  const label = t.category === 'rising' ? 'Up' : t.category === 'falling' ? 'Down' : 'Flat';
  return { category: t.category, label };
}

function deltaCaption(student: WidaStudent, domain: DomainKey): string {
  const prior = priorLevel(student, domain);
  const current = currentLevel(student, domain);
  if (prior == null || current == null) return 'First scored year on file';
  const delta = Math.round((current - prior) * 10) / 10;
  if (delta === 0) return 'No change vs last year';
  return `${delta > 0 ? '+' : ''}${delta.toFixed(1)} vs last year`;
}

function windowLabel(window: string, score: number | null): string {
  return `${window} ${score != null ? score.toFixed(1) : '—'}`;
}

function IndicatorRow({ name, scores }: { name: string; scores: WindowScores }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 1,
        borderBottom: `1px solid ${COLORS.hairlineSoft}`,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.ink }}>{name}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.ink }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{windowLabel('BOY', scores.boy)}</Typography>
        <NavigateNextIcon sx={{ fontSize: 16, color: COLORS.faint }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{windowLabel('MOY', scores.moy)}</Typography>
        <NavigateNextIcon sx={{ fontSize: 16, color: COLORS.faint }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{windowLabel('EOY', scores.eoy)}</Typography>
      </Box>
    </Box>
  );
}

function DomainCard({
  student,
  domain,
  flashlight,
  resetYear,
}: {
  student: PlanStudent;
  domain: DomainKey;
  flashlight: FlashlightData;
  resetYear: number | null;
}) {
  const criteria = useReclassCriteria();
  const level = currentLevel(student, domain);
  const trajectory: PeTrajectoryPoint[] = domainSeries(student, domain).map((p) => ({
    school_year: `${p.year - 1}-${p.year}`,
    average_scale_score: p.scale ?? 0,
    average_proficiency_level: p.level,
    reset_era: resetYear != null && p.year >= resetYear ? 'post_reset' : 'pre_reset',
  }));
  const pill = trendPill(student, domain);
  const below = belowExit(student, criteria, domain);
  const descriptor = currentDescriptor(domain, level);
  const hasFlashlight = flashlight.hasBenchmarks && isFlashlightDomain(domain);
  const benchmark = hasFlashlight ? flashlightBenchmark(flashlight, domain) : undefined;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 320,
        backgroundColor: COLORS.bg,
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.ink }}>
          {DOMAIN_LABELS[domain]}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!hasFlashlight && <Pill label="WIDA Only" bg={WIDA_ONLY_PILL.bg} fg={WIDA_ONLY_PILL.fg} />}
          <TrendPill category={pill.category} label={pill.label} />
          {below && <BelowExitPill />}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 34, fontWeight: 800, color: COLORS.ink, lineHeight: 1.05 }}>
          {level != null ? level.toFixed(1) : '—'}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: COLORS.muted }}>
          {deltaCaption(student, domain)}
        </Typography>
      </Box>

      {descriptor && (
        <>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>
            Can Do • {descriptor.label}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.55, color: COLORS.ink }}>
            {descriptor.text}
          </Typography>
        </>
      )}

      <Box data-testid="domain-trajectory-chart" sx={{ mt: 0.5 }}>
        {trajectory.length <= 1 ? (
          <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: COLORS.muted, py: 4, textAlign: 'center' }}>
            Not enough scored years to chart a trajectory.
          </Typography>
        ) : (
          <WidaTrajectoryChart
            trajectory={trajectory}
            unit="proficiency_level"
            variant="perDomain"
            accentColor={COLORS.indigo}
            fontSize={PER_DOMAIN_CHART_FONT_SIZE}
          />
        )}
      </Box>

      {benchmark && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, mb: 0.5 }}>
            Flashlight Indicators
          </Typography>
          {benchmark.indicators.map((indicator) => (
            <IndicatorRow key={indicator.name} name={indicator.name} scores={indicator.scores} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function DomainProfile({
  student,
  flashlight = getFlashlightData(),
}: {
  student: PlanStudent;
  flashlight?: FlashlightData;
}) {
  const resetYear = detectResetYear(student);
  return (
    <PlanCard title="Domain Profile" subtitle="Per-domain trajectory and current capability for each domain.">
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <DomainCard student={student} domain="listening" flashlight={flashlight} resetYear={resetYear} />
        <DomainCard student={student} domain="speaking" flashlight={flashlight} resetYear={resetYear} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <DomainCard student={student} domain="reading" flashlight={flashlight} resetYear={resetYear} />
        <DomainCard student={student} domain="writing" flashlight={flashlight} resetYear={resetYear} />
      </Box>
      <Box sx={{ pt: 0.5 }}>
        <SectionLabel>Source</SectionLabel>
        <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: COLORS.muted, mt: 0.5, lineHeight: 1.5 }}>
          Can Do descriptors adapted from WIDA Can Do Descriptors, Key Uses Edition. WIDA trajectories plot
          proficiency levels (1–6) for each scored administration; waived years are shown as gaps.
        </Typography>
      </Box>
    </PlanCard>
  );
}
